import { unref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { getImagesBySizes, getSizesPicture, getSrcSetByDPR } from '@devhacker/shared/utils/image';
import type { ImageSizesByDevice, Img } from '@devhacker/shared/utils/image/types';

type ImageSource = Img[] | Ref<Img[]> | ComputedRef<Img[]>;
type SizesSource = number[] | Ref<number[]> | ComputedRef<number[]>;
type SizesByDeviceSource =
  | ImageSizesByDevice
  | Ref<ImageSizesByDevice>
  | ComputedRef<ImageSizesByDevice>;
type EnabledSource = boolean | Ref<boolean> | ComputedRef<boolean>;

type UseArticleCardCoverPreloadHeadOptions = {
  enabled?: EnabledSource;
  images: ImageSource;
  sizes: SizesSource;
  sizesByDevice: SizesByDeviceSource;
};

export function useArticleCardCoverPreloadHead(options: UseArticleCardCoverPreloadHeadOptions) {
  if (import.meta.client) return;

  useHead(
    computed(() => ({
      link: unref(options.enabled) === false ? [] : makePreloadLinksForArticleCardCover(options),
    })),
  );
}

function makePreloadLinksForArticleCardCover({
  images,
  sizes,
  sizesByDevice,
}: UseArticleCardCoverPreloadHeadOptions): PreloadImageLink[] {
  try {
    const preloadLink = createPreloadLink(unref(images), unref(sizes), unref(sizesByDevice));
    return preloadLink ? [preloadLink] : [];
  } catch (error) {
    console.error(`Error in makePreloadLinksForArticleCardCover: ${error}`);
    return [];
  }
}

function createPreloadLink(
  images: Img[],
  sizes: number[],
  sizesByDevice: ImageSizesByDevice,
): PreloadImageLink | null {
  const [image] = getImagesBySizes(sizes, images ?? []);

  if (!image) return null;

  return {
    rel: 'preload',
    as: 'image',
    href: image.url,
    imagesrcset: getSrcSetByDPR(sizes, images),
    imagesizes: getSizesPicture(sizesByDevice),
  };
}

type PreloadImageLink = {
  rel: 'preload';
  as: 'image';
  href: string;
  imagesrcset: string;
  imagesizes: string;
};
